import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Coins, Zap, Trophy, Check, ArrowRight } from "lucide-react";

const STYLE = {
  bronze: { color: "#E08D3C", icon: Coins },
  silver: { color: "#C7D2DE", icon: Zap },
  gold: { color: "#F5C842", icon: Trophy },
};
const ORDER = { bronze: 1, silver: 2, gold: 3 };
const SPIRITUAL_LINKS = {
  bronze: [{ label: "Alignment", to: "/alignment" }, { label: "Mindful Games", to: "/mindful" }, { label: "Journey", to: "/journey" }],
  silver: [{ label: "Alignment", to: "/alignment" }, { label: "Mindful Games", to: "/mindful" }, { label: "Breathing", to: "/alignment" }, { label: "Journey", to: "/journey" }],
  gold: [{ label: "Alignment", to: "/alignment" }, { label: "Mindful Games", to: "/mindful" }, { label: "Insights", to: "/journey" }, { label: "Journey", to: "/journey" }],
};

export const PlanPanel = () => {
  const [tiers, setTiers] = useState([]);
  const [current, setCurrent] = useState("bronze");
  const [sub, setSub] = useState(null);
  const [pricing, setPricing] = useState({});

  useEffect(() => {
    api.get("/tiers").then((r) => setTiers(r.data.tiers)).catch(() => {});
    api.get("/pricing").then((r) => setPricing(r.data.tiers || {})).catch(() => {});
    api.get("/subscription").then((r) => {
      setSub(r.data.subscription);
      setCurrent(r.data.tier?.id || "bronze");
    }).catch(() => {});
  }, []);

  const expiry = sub?.expires_at ? new Date(sub.expires_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : null;

  return (
    <div data-testid="plan-panel">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm tracking-[0.25em] uppercase text-slate-400">Your Plan</p>
        <Link to="/dashboard" data-testid="manage-plan-link" className="text-xs text-slate-400 hover:text-slate-200 transition-colors duration-300">Manage →</Link>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {tiers.map((t) => {
          const s = STYLE[t.id] || STYLE.bronze;
          const Icon = s.icon;
          const isCurrent = t.id === current;
          const isUpgrade = ORDER[t.id] > ORDER[current];
          return (
            <div
              key={t.id}
              data-testid={`plan-tier-${t.id}`}
              className={`relative rounded-2xl border p-5 transition-all duration-300 ${isCurrent ? "bg-white/[0.05]" : "bg-white/[0.02] hover:-translate-y-0.5"}`}
              style={{ borderColor: isCurrent ? s.color : `${s.color}33`, boxShadow: isCurrent ? `0 0 40px ${s.color}22` : "none" }}
            >
              {isCurrent && (
                <span data-testid={`plan-current-${t.id}`} className="absolute -top-2.5 left-4 text-[10px] font-arcade tracking-wider px-2 py-0.5" style={{ background: s.color, color: "#0A0A14" }}>
                  YOUR PLAN
                </span>
              )}
              <div className="flex items-center justify-between mb-3">
                <Icon className="w-6 h-6" style={{ color: s.color }} />
                <span className="font-arcade text-sm" style={{ color: s.color }}>{t.tagline}</span>
              </div>
              <h3 className="font-head font-900 text-xl text-slate-50">{t.name}</h3>
              <div className="mt-1 mb-3">
                {t.free ? (
                  <span className="font-head font-700 text-lg" style={{ color: s.color }}>FREE</span>
                ) : (
                  <span className="font-head font-700 text-lg" style={{ color: s.color }}>
                    {t.price_btc} <span className="text-xs text-slate-400">BTC</span>
                    {pricing[t.id]?.usd != null && <span className="text-sm text-slate-300 ml-2" data-testid={`plan-usd-${t.id}`}>≈ ${pricing[t.id].usd}</span>}
                  </span>
                )}
              </div>
              <ul className="space-y-1.5 mb-3">
                {t.perks.slice(0, 3).map((p) => (
                  <li key={p} className="flex items-start gap-2 text-xs text-slate-400">
                    <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" style={{ color: s.color }} /> {p}
                  </li>
                ))}
              </ul>
              <div className="flex flex-wrap gap-1.5 mb-4" data-testid={`plan-links-${t.id}`}>
                {SPIRITUAL_LINKS[t.id].map((l) => (
                  <Link
                    key={l.label}
                    to={l.to}
                    data-testid={`plan-${t.id}-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                    className="text-[11px] px-2 py-0.5 rounded-full border transition-colors duration-200 hover:bg-white/5"
                    style={{ borderColor: `${s.color}55`, color: s.color }}
                  >
                    {l.label}
                  </Link>
                ))}
              </div>
              {isCurrent ? (
                <div className="text-xs text-slate-400" data-testid={`plan-status-${t.id}`}>
                  {expiry ? `Active until ${expiry}` : "Active — free forever"}
                </div>
              ) : isUpgrade ? (
                <Link to={`/pay/${t.id}`} data-testid={`plan-upgrade-${t.id}`} className="inline-flex items-center gap-1 text-xs font-medium px-3 py-1.5 rounded-lg transition-transform duration-300 hover:-translate-y-0.5" style={{ background: s.color, color: "#0A0A14" }}>
                  Upgrade <ArrowRight className="w-3 h-3" />
                </Link>
              ) : (
                <div className="text-xs text-slate-500" data-testid={`plan-status-${t.id}`}>Included</div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};
